// src/features/equipment/components/EquipmentForm.jsx
import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';

import { EQUIPMENT_TYPES, RADIOLOGY_TYPES } from '../utils/enums';
import { FormSection, FormRow } from '../../../components/common/form';

const EquipmentForm = memo(function EquipmentForm({
  formData,
  onInputChange,
  validationErrors = {},
  loading = false,
  mode = 'create', // 'create' or 'edit'
  showRoomInfo = true,
  roomName = null,
}) {
  const { t } = useTranslation(['equipment', 'common']);

  const isRadiologyEquipment = RADIOLOGY_TYPES.includes(formData.equipment_type);

  const handleNameChange = useCallback((event) => {
    onInputChange('name', event.target.value);
  }, [onInputChange]);

  const handleTypeChange = useCallback((event) => {
    onInputChange('equipment_type', event.target.value);
  }, [onInputChange]);

  const handleManufacturerChange = useCallback((event) => {
    onInputChange('manufacturer', event.target.value);
  }, [onInputChange]);

  const handleModelChange = useCallback((event) => {
    onInputChange('model', event.target.value);
  }, [onInputChange]);

  const handleSerialNumberChange = useCallback((event) => {
    onInputChange('serial_number', event.target.value);
  }, [onInputChange]);

  const handleBatchNumberChange = useCallback((event) => {
    onInputChange('batch_number', event.target.value);
  }, [onInputChange]);

  const handleMaxVoltageChange = useCallback((event) => {
    const value = event.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      onInputChange('max_voltage_kv', value);
    }
  }, [onInputChange]);

  const handleReceiptControlDateChange = useCallback((event) => {
    onInputChange('last_receipt_control_date', event.target.value);
  }, [onInputChange]);

  const handleNotesChange = useCallback((event) => {
    onInputChange('notes', event.target.value);
  }, [onInputChange]);

  return (
    <FormSection
      title={mode === 'create'
        ? t('equipment:form.equipment_information')
        : t('equipment:form.edit_equipment_information')
      }
    >
        {showRoomInfo && roomName && (
          <Alert severity="info">
            {t('equipment:form.creating_in_room', { roomName })}
          </Alert>
        )}

          <TextField
            fullWidth
            label={t('equipment:labels.equipment_name')}
            value={formData.name || ''}
            onChange={handleNameChange}
            error={!!validationErrors.name}
            helperText={validationErrors.name || t('equipment:form.name_helper')}
            required
            disabled={loading}
            placeholder={t('equipment:placeholders.name')}
            inputProps={{ maxLength: 255 }}
          />

          {mode === "create" && (
            <FormControl fullWidth required error={!!validationErrors.equipment_type}>
              <InputLabel id="equipment-form-equipment-type-label">{t('equipment:labels.equipment_type')}</InputLabel>
              <Select labelId="equipment-form-equipment-type-label"
                value={formData.equipment_type || ''}
                onChange={handleTypeChange}
                disabled={loading}
                label={t('equipment:labels.equipment_type')}
              >
                {EQUIPMENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(`equipment:equipment_types.${type}`)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {validationErrors.equipment_type || t('equipment:form.type_helper')}
              </FormHelperText>
            </FormControl>
          )}

          {isRadiologyEquipment && (
            <Alert severity="info">
              {t('equipment:form.radiology_info')}
            </Alert>
          )}

          <FormRow>
            <TextField
              fullWidth
              label={t('equipment:labels.manufacturer')}
              value={formData.manufacturer || ''}
              onChange={handleManufacturerChange}
              error={!!validationErrors.manufacturer}
              helperText={validationErrors.manufacturer || t('equipment:form.manufacturer_helper')}
              disabled={loading}
              placeholder={t('equipment:placeholders.manufacturer')}
              inputProps={{ maxLength: 255 }}
            />

            <TextField
              fullWidth
              label={t('equipment:labels.model')}
              value={formData.model || ''}
              onChange={handleModelChange}
              error={!!validationErrors.model}
              helperText={validationErrors.model || t('equipment:form.model_helper')}
              disabled={loading}
              placeholder={t('equipment:placeholders.model')}
              inputProps={{ maxLength: 255 }}
            />
          </FormRow>

          <FormRow>
            <TextField
              fullWidth
              label={t('equipment:labels.serial_number')}
              value={formData.serial_number || ''}
              onChange={handleSerialNumberChange}
              error={!!validationErrors.serial_number}
              helperText={validationErrors.serial_number || t('equipment:form.serial_number_helper')}
              disabled={loading}
              placeholder={t('equipment:placeholders.serial_number')}
              inputProps={{ maxLength: 255 }}
            />

            <TextField
              fullWidth
              label={t('equipment:labels.batch_number')}
              value={formData.batch_number || ''}
              onChange={handleBatchNumberChange}
              error={!!validationErrors.batch_number}
              helperText={validationErrors.batch_number || t('equipment:form.batch_number_helper')}
              disabled={loading}
              placeholder={t('equipment:placeholders.batch_number')}
              inputProps={{ maxLength: 255 }}
            />
          </FormRow>

          {isRadiologyEquipment && (
            <FormRow>
              <TextField
                fullWidth
                type="number"
                label={t('equipment:labels.max_voltage_kv')}
                value={formData.max_voltage_kv || ''}
                onChange={handleMaxVoltageChange}
                error={!!validationErrors.max_voltage_kv}
                helperText={validationErrors.max_voltage_kv || t('equipment:form.max_voltage_helper')}
                disabled={loading}
                placeholder="70"
                inputProps={{ 
                  step: "0.01",
                  min: "0",
                }}
              />

              <TextField
                fullWidth
                type="date"
                label={t('equipment:labels.last_receipt_control_date')}
                value={formData.last_receipt_control_date || ''}
                onChange={handleReceiptControlDateChange}
                error={!!validationErrors.last_receipt_control_date}
                helperText={validationErrors.last_receipt_control_date || t('equipment:form.receipt_control_helper')}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormRow>
          )}

          <TextField
            fullWidth
            label={t('equipment:labels.notes')}
            value={formData.notes || ''}
            onChange={handleNotesChange}
            error={!!validationErrors.notes}
            helperText={validationErrors.notes || t('equipment:form.notes_helper')}
            disabled={loading}
            placeholder={t('equipment:placeholders.notes')}
            multiline
            rows={4}
          />

        {mode === 'create' && (
          <Alert severity="info">
            {t('equipment:form.after_creation_info')}
          </Alert>
        )}
    </FormSection>
  );
});

export default EquipmentForm;